import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy, useEffect } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { ApiError, tokenStore } from './lib/api'
import { AsOfProvider } from './lib/as-of'
import { SessionProvider, useSession } from './lib/session'
import { ErrorBoundary } from './components/error-boundary'
import { Layout } from './components/layout'
import { Skeleton } from './components/ui'
import { SignIn } from './routes/sign-in'

/*
 * Routes are loaded on demand, and the loaders are named so they can also be
 * started early.
 *
 * Sign-in is the exception and stays eager: it is what an unauthenticated
 * visitor always gets, so fetching it in a second step would be a spinner
 * before a form.
 */
const load = {
  dashboard: () => import('./routes/dashboard'),
  people: () => import('./routes/people'),
  employee: () => import('./routes/employee'),
  bands: () => import('./routes/bands'),
  equity: () => import('./routes/equity'),
  simulate: () => import('./routes/simulate'),
  audit: () => import('./routes/audit'),
  import: () => import('./routes/import'),
}

const Dashboard = lazy(() => load.dashboard().then((m) => ({ default: m.Dashboard })))
const People = lazy(() => load.people().then((m) => ({ default: m.People })))
const Employee = lazy(() => load.employee().then((m) => ({ default: m.Employee })))
const Bands = lazy(() => load.bands().then((m) => ({ default: m.Bands })))
const Equity = lazy(() => load.equity().then((m) => ({ default: m.Equity })))
const Simulate = lazy(() => load.simulate().then((m) => ({ default: m.Simulate })))
const Audit = lazy(() => load.audit().then((m) => ({ default: m.Audit })))
const ImportRound = lazy(() => load.import().then((m) => ({ default: m.ImportRound })))

/**
 * Starts downloading route code before anything renders it.
 *
 * A lazy import only begins when its component is first rendered, and nothing
 * renders until the session has been verified. The measured critical chain was
 * therefore: fetch the entry bundle, ask the server who we are, *then* fetch
 * the dashboard chunk, then fetch its data — four round trips in sequence, two
 * of them avoidable, and on a 190ms link that is most of a second of waiting
 * to be told things that could have been asked at once.
 *
 * Anybody holding a token will need the dashboard, so its chunk is fetched in
 * parallel with the verification rather than after it. The rest are fetched
 * when the browser next goes idle, which is close to free: by the time somebody
 * clicks "People" the code is already there, and a visitor who never clicks it
 * has paid nothing they were otherwise using.
 */
function usePrefetchedRoutes(signedIn: boolean): void {
  useEffect(() => {
    if (!signedIn) return

    void load.dashboard()

    const rest = () => {
      void load.people()
      void load.employee()
      void load.bands()
      void load.equity()
      void load.simulate()
    }

    // requestIdleCallback is still missing in Safari, so a timeout is the
    // honest fallback rather than a polyfill for one call.
    // Called on `window` rather than lifted into a variable first: detaching a
    // host method from its object is how you get a `this` that is undefined in
    // strict mode, and the engine is under no obligation to be forgiving.
    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(rest, { timeout: 3000 })
      return () => window.cancelIdleCallback?.(handle)
    }

    const timer = setTimeout(rest, 1500)
    return () => clearTimeout(timer)
  }, [signedIn])
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    /*
     * A dead session is handled once, here, rather than in every panel.
     *
     * Without this, an expired token means each card on the dashboard renders
     * its own "Sign in to continue" while the shell still shows your name in
     * the corner. Clearing the token sends the whole application back to the
     * sign-in screen, which is the truth.
     */
    onError: (error) => {
      if (error instanceof ApiError && error.isUnauthorized) tokenStore.clear()
    },
  }),
  defaultOptions: {
    queries: {
      // Salary data is not realtime, and refetching the roster because somebody
      // switched browser tabs is a query over 10,000 people for no new answer.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Retrying a 401, 403 or 409 cannot succeed; it only delays the message.
        if (error instanceof ApiError && error.status < 500) return false
        return failureCount < 2
      },
    },
  },
})

/**
 * The outermost boundary wraps the providers, not just the screens.
 *
 * There is a second one inside the layout, around whichever screen is
 * showing, and for a while that was the only one. It cannot catch what mounts
 * above it: when the query client provider itself threw, React found no
 * boundary, unmounted the whole tree, and left a blank white page with an
 * explanation only in the console. The boundary meant to prevent that was
 * sitting inside the component that failed.
 *
 * It lives here rather than in `main.tsx` so that the tests, which render
 * `<App />`, exercise the real one.
 */
export function App() {
  return (
    <ErrorBoundary label="the application" scope="application">
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <AsOfProvider>
            <BrowserRouter>
              <Shell />
            </BrowserRouter>
          </AsOfProvider>
        </SessionProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

function Shell() {
  const { user, loading, isAdmin } = useSession()

  // Started while the session is still being verified, not after it.
  usePrefetchedRoutes(Boolean(tokenStore.read()))

  // Deciding whether a stored token is still good takes one request. Rendering
  // the sign-in screen in the meantime would flash it at somebody who is
  // already signed in.
  if (loading) {
    return (
      <div className="mx-auto max-w-[1400px] space-y-3 p-6">
        <Skeleton className="h-10" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  if (!user) return <SignIn />

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Dashboard />} />
          <Route path="people" element={<People />} />
          <Route path="people/:id" element={<Employee />} />
          <Route path="bands" element={<Bands />} />
          <Route path="equity" element={<Equity />} />
          <Route path="simulate" element={<Simulate />} />
          {isAdmin ? <Route path="audit" element={<Audit />} /> : null}
          {isAdmin ? <Route path="import" element={<ImportRound />} /> : null}
          <Route path="*" element={<Dashboard />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

function RouteFallback() {
  return (
    <div className="mx-auto max-w-[1400px] space-y-3 p-6">
      <Skeleton className="h-24" />
      <Skeleton className="h-64" />
    </div>
  )
}
